import {
  Box,
  Button,
  DatePicker,
  FormField,
  Input,
  Modal,
  Select,
  type SelectProps,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { Dispatch, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Agp } from '../../api/agp';
import { Filter, createItem, deleteItem } from '../../api/generic';
import { useAlertContext } from '../../contexts/AlertContext';
import { useAuthContext } from '../../contexts/AuthContext';
import useSubscribe from '../../hooks/useSubscribe';
import { Batch } from '../../models/Batch';
import { Recipe } from '../../models/Recipe';
import { prefixCollection } from '../../utils/prefix';
import { toDate } from '../../utils/timestamp';
import { Space } from '../Flex';

interface ReleaseModalProps {
  visible: boolean;
  setVisible: Dispatch<boolean>;
  batchId?: string;
  batchMap?: Record<string, Batch>;
  recipeMap?: Record<string, Recipe>;
  isVoid?: boolean;
}

const keyFor = (a: Pick<Agp, 'batchId' | 'name' | 'size'>) => `${a.batchId}::${a.name}::${a.size}`;
const groupKeyFor = (a: Pick<Agp, 'batchId' | 'size'>) => `${a.batchId}::${a.size}`;

type GroupRow = {
  // one per (batchId, size)
  key: string; // `${batchId}::${size}`
  batchId: string;
  size: number;
  name: string; // representative name (latest IN’s name)
  createdDate?: Date; // latest IN created date
  note?: string; // latest IN note
};

const ReleaseModal: FC<ReleaseModalProps> = ({ visible, setVisible, batchId, batchMap, recipeMap, isVoid }) => {
  const alert = useAlertContext();
  const { tenant, userObject } = useAuthContext();

  // All AGPs (for dropdown)
  const [allAgps, setAllAgps] = useState<Agp[]>([]);
  const [error, setError] = useState<Error>();

  const sortByCreated = useCallback(
    (a: Agp, b: Agp) => toDate(a.createdDate).getTime() - toDate(b.createdDate).getTime(),
    [],
  );

  // Always-on subscription: all AGPs for dropdown
  useSubscribe(prefixCollection('agp', tenant), setAllAgps, setError, sortByCreated, []);

  const getSlugForBatch = (
    batchMap?: Record<string, Batch>,
    recipeMap?: Record<string, Recipe>,
    bid?: string,
  ): string => {
    if (!bid) return 'unknown';
    const b = batchMap?.[bid];
    if (!b) return 'unknown';
    return (
      b.recipe?.slug ?? (b as any).recipeSlug ?? (b.recipeId ? recipeMap?.[b.recipeId]?.slug : undefined) ?? 'unknown'
    );
  };

  const formatBatchLabel = (
    bid: string,
    batchMap?: Record<string, Batch>,
    recipeMap?: Record<string, Recipe>,
  ): string => {
    if (!bid) return '—';
    const slug = getSlugForBatch(batchMap, recipeMap, bid);
    const short = batchMap?.[bid]?.batchId ?? bid.slice(-6);
    const name = batchMap?.[bid]?.name ?? '';
    return [slug, short, name].filter(Boolean).join(' — ');
  };

  // Dropdown options
  const batchOptions: SelectProps.Options = useMemo(() => {
    const byBatchLatest = new Map<string, number>();
    for (const a of allAgps) {
      const bid = a.batchId;
      const t = toDate(a.createdDate).getTime();
      if (!byBatchLatest.has(bid) || t > (byBatchLatest.get(bid) ?? 0)) {
        byBatchLatest.set(bid, t);
      }
    }
    return Array.from(byBatchLatest.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([bid]) => ({
        value: bid,
        label: formatBatchLabel(bid, batchMap, recipeMap),
      }));
  }, [allAgps, batchMap, recipeMap]);

  // Selection state
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(batchId ?? null);
  const selectedOption = useMemo<SelectProps.Option | null>(
    () => batchOptions.find((o) => o.value === selectedBatchId) ?? null,
    [batchOptions, selectedBatchId],
  );

  // Init/reconcile when modal opens or prop changes
  useEffect(() => {
    if (!visible) return;
    const preferred = batchId ?? selectedBatchId ?? batchOptions[0]?.value ?? null;
    if (preferred !== (selectedBatchId ?? null)) setSelectedBatchId(preferred);
  }, [visible, batchId, batchOptions]);

  // Subscribe to rows for selected batch
  const [agps, setAgps] = useState<Agp[]>([]);
  const batchFilter: Filter<Agp> | undefined = selectedBatchId
    ? { field: 'batchId', operator: '==', value: selectedBatchId }
    : undefined;

  // Clear agps when deselected
  useEffect(() => {
    if (!selectedBatchId) setAgps([]);
  }, [selectedBatchId]);

  useSubscribe(
    prefixCollection('agp', tenant),
    setAgps,
    setError,
    (a, b) => toDate(a.createdDate).getTime() - toDate(b.createdDate).getTime(),
    batchFilter ? [batchFilter] : undefined,
    !!selectedBatchId, // only subscribe when a batch is selected (useSubscribe supports enabled flag)
  );

  // ---------- Grouping & totals (distinct on batchId + size) ----------

  // Totals per (batchId, size)
  const totalsByGroup = useMemo(() => {
    const map: Record<string, { in: number; out: number; void: number }> = {};
    for (const a of agps) {
      const k = groupKeyFor(a);
      if (!map[k]) map[k] = { in: 0, out: 0, void: 0 };

      const amount = a.amount ?? 0;
      const isReleased = !!a.releasedDate;
      const isVoided = !!a.voidedDate;

      if (isVoided) {
        // VOID
        map[k].void += Math.abs(amount);
      } else if (isReleased) {
        // normal OUT
        map[k].out += Math.abs(amount);
      } else if (amount < 0) {
        // negative IN → treat as OUT
        map[k].out += Math.abs(amount);
      } else {
        // normal IN
        map[k].in += amount;
      }
    }
    return map;
  }, [agps]);

  // One display row per (batchId, size) from latest IN
  const unreleasedGrouped: GroupRow[] = useMemo(() => {
    const byKey = new Map<string, GroupRow>();
    for (const a of agps) {
      if (a.releasedDate) continue; // only INs
      const k = groupKeyFor(a);
      const cd = toDate(a.createdDate);
      const existing = byKey.get(k);
      if (!existing || cd.getTime() > (existing.createdDate?.getTime() ?? 0)) {
        byKey.set(k, {
          key: k,
          batchId: a.batchId,
          size: a.size,
          name: a.name,
          createdDate: cd,
        });
      }
    }
    return Array.from(byKey.values()).sort((a, b) => {
      if (b.size !== a.size) return b.size - a.size;
      return (b.createdDate?.getTime() ?? 0) - (a.createdDate?.getTime() ?? 0);
    });
  }, [agps]);

  // ---------- Releasing amounts keyed by group ----------

  const [releaseAmounts, setReleaseAmounts] = useState<Record<string, number>>({}); // key = groupKey

  const availableForGroup = (row: GroupRow) => {
    const tot = totalsByGroup[row.key] ?? { in: 0, out: 0 };
    const available = tot.in - tot.out;
    const pending = Object.entries(releaseAmounts)
      .filter(([k]) => k === row.key)
      .reduce((s, [, v]) => s + (v ?? 0), 0);
    return Math.max(available - pending + (releaseAmounts[row.key] ?? 0), 0);
  };

  const handleAmountChangeGroup = (row: GroupRow, value: string) => {
    const parsed = Number(value);
    const max = availableForGroup(row);
    if (Number.isFinite(parsed) && parsed >= 0) {
      setReleaseAmounts((prev) => ({ ...prev, [row.key]: Math.min(parsed, max) }));
    } else if (value === '') {
      setReleaseAmounts((prev) => ({ ...prev, [row.key]: 0 }));
    }
  };

  // Unreleased Inventory Adjustments (note === "Inventory adjustment")
  const inventoryAdjustments = useMemo(
    () => agps.filter((a) => !a.releasedDate && a.note?.trim().toLowerCase() === 'inventory adjustment'),
    [agps],
  );

  // Inputs
  const [releaseDate, setReleaseDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');

  const handleRelease = async () => {
    if (!selectedBatchId) {
      alert.info('Choose a batch first.');
      return;
    }
    const candidates = unreleasedGrouped
      .map((row) => ({ row, qty: releaseAmounts[row.key] ?? 0 }))
      .filter((x) => x.qty > 0);

    if (candidates.length === 0) return alert.info('Nothing to release.');

    // Validate
    for (const { row, qty } of candidates) {
      const max = availableForGroup(row);
      if (qty > max) {
        return alert.error(`Cannot release ${qty} of size ${row.size}L. Available: ${max}.`);
      }
    }

    // Create one OUT record per group
    for (const { row, qty } of candidates) {
      const payload: Omit<Agp, 'id'> = {
        batchId: row.batchId,
        name: row.name, // latest IN’s name for this size
        size: row.size,
        amount: qty,
        note: note ?? '',
        createdBy: userObject?.id,
        createdDate: new Date(),
        direction: isVoid ? 'VOID' : 'OUT',
      };

      if (isVoid) {
        payload.voidedDate = releaseDate ? new Date(releaseDate) : new Date();
      } else {
        payload.releasedDate = releaseDate ? new Date(releaseDate) : new Date();
      }

      await createItem(prefixCollection('agp', tenant), payload);
    }

    setNote('');
    setReleaseAmounts({});
    alert.success(isVoid ? 'Void movements recorded.' : 'Release movements recorded.');
  };

  const handleDeleteReleased = async (id?: string) => {
    if (!id) return;
    if (!window.confirm('Remove this released entry?')) return;
    await deleteItem(prefixCollection('agp', tenant), id);
  };

  // Lower tables
  const releasedOnly = useMemo(() => agps.filter((a) => !!a.releasedDate && (!a.sold || a.sold === 0)), [agps]);
  const sold = useMemo(() => agps.filter((a) => !!a.releasedDate && (a.sold ?? 0) > 0), [agps]);
  const voided = useMemo(() => agps.filter((a) => !!a.voidedDate), [agps]);

  return (
    <Modal
      header={isVoid ? 'Void bottles' : 'Release bottles'}
      visible={visible}
      size="large"
      onDismiss={() => setVisible(false)}
    >
      <SpaceBetween size="m">
        <Box variant="awsui-key-label">
          Mark bottles as <b>{isVoid ? 'voided' : 'released'}</b>. This adds immutable {isVoid ? 'VOID' : 'OUT'}{' '}
          records. Existing IN records are never changed.
        </Box>

        {/* Batch picker */}
        {!batchId && (
          <FormField label="Batch" description="Can't find your batch? Push to AGP from the batch page.">
            <Select
              options={batchOptions}
              selectedOption={selectedOption}
              onChange={({ detail }) => {
                const v = detail.selectedOption?.value ?? null;
                setSelectedBatchId(v);
                setReleaseAmounts({});
              }}
              placeholder="Choose batch"
              filteringType="auto"
            />
          </FormField>
        )}

        {!selectedBatchId && <Box>Select a batch to view and release bottles.</Box>}

        {selectedBatchId && (
          <Table<GroupRow>
            columnDefinitions={[
              { id: 'name', header: 'Name', cell: (r) => r.name },
              { id: 'size', header: 'Size (L)', cell: (r) => r.size },
              { id: 'in', header: 'IN', cell: (r) => totalsByGroup[r.key]?.in ?? 0 },
              { id: 'out', header: 'OUT', cell: (r) => totalsByGroup[r.key]?.out ?? 0 },
              { id: 'void', header: 'VOID', cell: (r) => totalsByGroup[r.key]?.void ?? 0 },
              { id: 'avail', header: 'In AGP', cell: (r) => availableForGroup(r) },
              {
                id: isVoid ? 'void' : 'release',
                header: isVoid ? 'Void' : 'Release',
                minWidth: 130,
                cell: (r) => {
                  const v = releaseAmounts[r.key] ?? 0;
                  const max = availableForGroup(r);
                  return (
                    <Input
                      type="number"
                      inputMode="numeric"
                      value={v.toString()}
                      disabled={max <= 0}
                      placeholder={max > 0 ? `Max ${max}` : 'No bottles'}
                      onChange={({ detail }) => handleAmountChangeGroup(r, detail.value)}
                    />
                  );
                },
              },
              {
                id: 'createdDate',
                header: 'Latest IN',
                cell: (r) => (r.createdDate ? r.createdDate.toLocaleDateString() : '—'),
              },
            ]}
            items={unreleasedGrouped} // <-- single row per (batchId, size)
            trackBy="key"
            empty="No unreleased bottles"
          />
        )}

        <SpaceBetween size="m" direction="horizontal" alignItems="end">
          <FormField label="Release Date">
            <DatePicker value={releaseDate} onChange={({ detail }) => setReleaseDate(detail.value)} />
          </FormField>
          <FormField label="Note (optional)">
            <Input
              value={note}
              onChange={({ detail }) => setNote(detail.value)}
              placeholder={isVoid ? 'e.g., Spoilage' : 'e.g., Festival stock'}
            />
          </FormField>
          <Space w={50} />
          <Button variant="primary" iconName="arrow-down" onClick={handleRelease} disabled={!selectedBatchId}>
            {isVoid ? 'Void' : 'Release'}
          </Button>
        </SpaceBetween>

        {selectedBatchId && (
          <>
            <Box variant="awsui-key-label">Inventory Adjustments (Unreleased)</Box>
            <Table<Agp>
              columnDefinitions={[
                { id: 'name', header: 'Name', cell: (i) => i.name },
                { id: 'size', header: 'Size (L)', cell: (i) => i.size },
                { id: 'amount', header: 'Amount (±)', cell: (i) => i.amount },
                {
                  id: 'createdDate',
                  header: 'Created',
                  cell: (i) => toDate(i.createdDate).toLocaleDateString(),
                },
                { id: 'note', header: 'Note', cell: (i) => i.note ?? '—' },
                {
                  id: 'remove',
                  header: '',
                  minWidth: 50,
                  cell: (i) => (
                    <Button
                      iconName="close"
                      variant="icon"
                      onClick={() => handleDeleteReleased(i.id)}
                      ariaLabel="Delete adjustment"
                    />
                  ),
                },
              ]}
              items={inventoryAdjustments}
              trackBy="id"
              stickyHeader
              empty="No inventory adjustments"
            />
          </>
        )}

        {selectedBatchId && (
          <>
            <Box variant="awsui-key-label">Already Released Bottles</Box>
            <Table<Agp>
              columnDefinitions={[
                { id: 'name', header: 'Name', cell: (i) => i.name },
                { id: 'amount', header: 'Amount', cell: (i) => i.amount },
                { id: 'size', header: 'Size (L)', cell: (i) => i.size },
                {
                  id: 'releasedDate',
                  header: 'Released',
                  cell: (i) => toDate(i.releasedDate).toLocaleDateString(),
                },
                { id: 'note', header: 'Note', cell: (i) => i.note ?? '—' },
                {
                  id: 'remove',
                  header: '',
                  cell: (i) => <Button iconName="close" variant="icon" onClick={() => handleDeleteReleased(i.id)} />,
                  minWidth: 50,
                },
              ]}
              items={releasedOnly}
              trackBy="id"
              empty="No released bottles"
            />

            <Box variant="awsui-key-label">Sold Bottles</Box>
            <Table<Agp>
              columnDefinitions={[
                { id: 'name', header: 'Name', cell: (i) => i.name },
                { id: 'amount', header: 'Amount', cell: (i) => i.amount },
                { id: 'size', header: 'Size (L)', cell: (i) => i.size },
                {
                  id: 'releasedDate',
                  header: 'Released',
                  cell: (i) => toDate(i.releasedDate).toLocaleDateString(),
                },
                { id: 'note', header: 'Note', cell: (i) => i.note ?? '—' },
                {
                  id: 'remove',
                  header: '',
                  cell: (i) => <Button iconName="close" variant="icon" onClick={() => handleDeleteReleased(i.id)} />,
                  minWidth: 50,
                },
              ]}
              items={sold}
              trackBy="id"
              empty="No sold bottles"
            />
            {/* Voided Bottles */}
            <Box variant="awsui-key-label">Voided Bottles</Box>
            <Table<Agp>
              columnDefinitions={[
                { id: 'name', header: 'Name', cell: (i) => i.name },
                { id: 'amount', header: 'Amount', cell: (i) => i.amount },
                { id: 'size', header: 'Size (L)', cell: (i) => i.size },
                {
                  id: 'voidedDate',
                  header: 'Voided',
                  cell: (i) => toDate(i.voidedDate).toLocaleDateString(),
                },
                { id: 'note', header: 'Note', cell: (i) => i.note ?? '—' },
                {
                  id: 'remove',
                  header: '',
                  cell: (i) => <Button iconName="close" variant="icon" onClick={() => handleDeleteReleased(i.id)} />,
                  minWidth: 50,
                },
              ]}
              items={voided}
              trackBy="id"
              empty="No voided bottles"
            />
          </>
        )}

        <Button onClick={() => setVisible(false)}>Close</Button>
      </SpaceBetween>
    </Modal>
  );
};

export default ReleaseModal;
